/* eslint-disable camelcase */
import React, { useEffect, useMemo, useRef } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup,
  Card,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '~/pages/Quality/styles';
import {
  getSieveOpenings,
  getMaterialSpecifications,
  addMaterialSpecification,
  setMaterialSpecificationValue,
  resetMaterialSpecifications,
  updateMaterialSpecifications,
} from '~/store/quality/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MOutlinedInput from '~/components/PF2MOutlinedInput';
import { Field, Form, Formik } from 'formik';
import { getMaterials } from '~/store/nref/actions';
import PF2MAddButton from '~/components/PF2MAddButton';
import PF2MDiscardChangesButton from '~/components/PF2MDiscardChangesButton';

const MaterialSpecsTab = ({ classes }) => {
  const sieveOpenings = useSelector(state => state.quality.sieveOpenings);
  const materialSpecifications = useSelector(state => state.quality.materialSpecifications);
  const tableData = useMemo(() => materialSpecifications
    .filter(ms => ms.active), [materialSpecifications]);
  const materials = useSelector(state => state.nref.materials);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  useEffect(() => {
    async function fetchData() {
      dispatch(getSieveOpenings());
      dispatch(getMaterials());
      dispatch(getMaterialSpecifications());
    }
    fetchData();
  }, [dispatch]);

  const columns = [
    {
      field: 'material_id',
      title: translate('common:Material'),
      lookup: materials,
      customType: 'filterLookup',
    },
    {
      field: 'upper_sieve_opening_id',
      title: translate('common:UpperSieveOpening'),
      lookup: sieveOpenings,
      customType: 'filterLookup',
      filtering: false,
    },
    {
      field: 'upper_limit_percentage',
      title: translate('common:UpperLimitPercentage'),
      filtering: false,
    },
    {
      field: 'lower_sieve_opening_id',
      title: translate('common:LowerSieveOpening'),
      lookup: sieveOpenings,
      customType: 'filterLookup',
      filtering: false,
    },
    {
      field: 'lower_limit_percentage',
      title: translate('common:LowerLimitPercentage'),
      filtering: false,
    },
    {
      field: 'control_sieves',
      title: translate('common:ControlSieves'),
      width: '100%',
      filtering: false,
      editable: false,
      render: rowData => (
        <PF2MSearchSelectOutlined
          value={rowData.control_sieves || []}
          multiple
          onChange={(e) => {
            dispatch(setMaterialSpecificationValue({
              ...rowData, control_sieves: [...e.target.value.map(r => r.value)],
            }));
          }}
          closeMenuOnSelect={false}
          isClearable={false}
          className={classes.typeSelector}
          placeholder={translate('common:Select')}
        >
          {sieveOpenings.map(e => ({ value: e.id, label: e.name }))}
        </PF2MSearchSelectOutlined>
      ),
    },
  ];

  const INITIAL_VALUES = {
    material_id: 0,
    upper_sieve_opening_id: 0,
    lower_sieve_opening_id: 0,
    upper_limit_percentage: 0,
    lower_limit_percentage: 0,
    control_sieves: [],
  };

  const renderItems = data => [
    { value: 0, label: translate('common:Select') },
    ...data.map(e => ({ value: e.id, label: e.name })),
  ];

  const specsForm = () => (
    <Formik
      initialValues={INITIAL_VALUES}
      validate={(values) => {
        const errors = {};

        // Verifica se existe um movimento com as informações fornecidas

        if (values.upper_sieve_opening_id <= 0) {
          errors.upper_sieve_opening_id = 'Required';
        }
        if (values.lower_sieve_opening_id <= 0) {
          errors.lower_sieve_opening_id = 'Required';
        }
        if (values.material_id === 0) {
          errors.material_id = 'Required';
        }
        if (values.upper_limit_percentage < 0) {
          errors.upper_limit_percentage = 'Required';
        }
        if (values.lower_limit_percentage < 0) {
          errors.lower_limit_percentage = 'Required';
        }
        if (values.control_sieves.length === 0) {
          errors.control_sieves = 'Required';
        }
        return errors;
      }}
      onSubmit={async (values, { resetForm }) => {
        // await add(values);
        dispatch(addMaterialSpecification(values));
        resetForm();
      }}
    >
      {({
        resetForm,
        isSubmitting,
        isValid,
        errors,
      }) => (
        <Form className="col-10">
          <p>{translate('common:MaterialSpecifications')}</p>
          <div className={classes.materialSpecsForm}>
            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:Material')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="material_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      onChange={
                        e => form.setFieldValue('material_id', e.target.value)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(materials)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:UpperSieveOpening')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="upper_sieve_opening_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      onChange={
                        e => form.setFieldValue('upper_sieve_opening_id', e.target.value)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(sieveOpenings)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:UpperLimitPercentage')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="upper_limit_percentage"
                  type="number"
                  className={classnames('form-control', classes.textField, errors.upper_limit_percentage
                    ? classes.fieldError : classes.typeSelector)}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:LowerSieveOpening')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="lower_sieve_opening_id"
                  render={({ field, form }) => (
                    <PF2MSearchSelectOutlined
                      {...field}
                      value={(field.value || 0)}
                      onChange={
                        e => form.setFieldValue('lower_sieve_opening_id', e.target.value)
                      }
                      className={classnames(classes.typeSelector, form.errors[field.name]
                        ? classes.fieldError : classes.typeSelector)}
                    >
                      {renderItems(sieveOpenings)}
                    </PF2MSearchSelectOutlined>
                  )}
                />
              </FormGroup>
            </FormControl>

            <FormControl>
              <FormLabel className={classes.formLabel}>
                {`${translate('common:LowerLimitPercentage')}:`}
              </FormLabel>
              <FormGroup>
                <Field
                  name="lower_limit_percentage"
                  type="number"
                  className={classnames('form-control', classes.textField, errors.lower_limit_percentage
                    ? classes.fieldError : classes.typeSelector)}
                />
              </FormGroup>
            </FormControl>
          </div>

          <p>{translate('common:ControlParams')}</p>

          <FormControl style={{ width: '100%' }}>
            <FormLabel className={classes.formLabel}>
              {`${translate('common:ControlSieves')}:`}
            </FormLabel>
            <FormGroup>
              <Field
                name="control_sieves"
                render={({ field, form }) => (
                  <PF2MSearchSelectOutlined
                    {...field}
                    value={(field.value || [])}
                    multiple
                    onChange={(e) => {
                      form.setFieldValue('control_sieves', [...e.target.value.map(r => r.value)]);
                    }}
                    closeMenuOnSelect={false}
                    className={classnames(classes.typeSelector, form.errors[field.name]
                      ? classes.fieldError : classes.typeSelector)}
                    placeholder={translate('common:Select')}
                  >
                    {sieveOpenings.map(e => ({ value: e.id, label: e.name }))}
                  </PF2MSearchSelectOutlined>
                )}
              />
            </FormGroup>
          </FormControl>

          <PF2MAddButton disabled={!isValid} />
          <PF2MDiscardChangesButton onClick={() => { resetForm(); }} />

        </Form>
      )}
    </Formik>
  );

  return (
    <div className={classes.tabContainer} style={{ overflowY: 'auto', height: 'calc(100vh - 120px)' }}>
      <p>{translate('common:AssociationRegistry')}</p>
      <Card variant={'outlined'} className={classes.card}>
        {specsForm()}

      </Card>
      <Card variant={'outlined'} className={classes.card} style={{ marginTop: '20px' }}>
        <PF2MDataTable
          onDiscard={() => dispatch(resetMaterialSpecifications())}
          onChange={(newData) => {
            newData.forEach((row) => {
              dispatch(setMaterialSpecificationValue(row));
            });
          }}
          onDelete={(items) => {
            items.forEach((item) => {
              dispatch(setMaterialSpecificationValue({ ...item, active: 0 }));
            });
          }}
          onAdd={newData => dispatch(addMaterialSpecification(newData))}
          onApply={() => dispatch(updateMaterialSpecifications(materialSpecifications))}
          options={{
            selection: true,
            showTitle: false,
            filtering: true,
          }}
          data={tableData}
          columns={columns}
        />
      </Card>
    </div>
  );
};

MaterialSpecsTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MaterialSpecsTab);
